import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit, ViewEncapsulation,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  PREP_TOUR_LABELS, PrepTourDefinition, PrepTourDialogContent, PrepTourLabels, PrepTourStep, prepTourText,
} from './tour.types';
import { PrepTourAnchorRegistry } from './tour-anchor-registry.service';
import { PrepTourService } from './tour.service';

const EDITABLE = /^(INPUT|TEXTAREA|SELECT)$/;
/** How often reactive texts / `hideWhile` / `busy` are re-read while a step is shown (outside the zone). */
const RENDER_POLL_MS = 150;

interface OutletVm {
  guide: PrepTourDefinition;
  phase: 'welcome' | 'step' | 'done' | null;
  welcome: PrepTourDialogContent | null;
  step: PrepTourStep | null;
  index: number;
  count: number;
  rect: DOMRect | null;
  anchorEl: HTMLElement | null;
  focusEl: HTMLElement | null;
  hidden: boolean;
  missing: boolean;
  minimized: boolean;
  canNext: boolean;
  completionCard: PrepTourDialogContent | null;
  announcement: string;
  restoreText: string;
  tick: number;
}

/**
 * The single host layer for guidance. Drop it ONCE inside the element that
 * goes fullscreen (or the layout root): everything it renders — spotlight,
 * coach-mark, welcome/completion dialogs, the live region — stays inside that
 * element and above the host's own overlays (`zIndex`).
 *
 * Keyboard (capture phase, only while a guide is active):
 *  Esc → stop · Enter / → → next (Next-able steps) · ← → back.
 * Keys typed into inputs are left alone except Esc.
 *
 * Rendering: the machine's `changes$` re-renders at once; a cheap poll
 * outside the Angular zone re-reads reactive texts, `hideWhile`, `busy` and
 * the anchor rect and only re-enters the zone when something differs.
 */
@Component({
  selector: 'prep-tour-outlet',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'prep-tour-outlet' },
  template: `
    <ng-container *ngIf="vm as v">
      <div class="prep-tour-layer" [style.z-index]="zIndex">
        <ng-container [ngSwitch]="v.phase">
          <ng-container *ngSwitchCase="'welcome'">
            <prep-tour-dialog *ngIf="v.welcome" [content]="v.welcome" [centered]="!!v.welcome.centered"
              [reducedMotion]="reducedMotion" [labels]="labels"
              (primary)="tour.begin()" (secondary)="tour.dismiss()"></prep-tour-dialog>
          </ng-container>
          <ng-container *ngSwitchCase="'step'">
            <ng-container *ngIf="v.step as step">
              <div *ngIf="v.minimized; else shown" class="prep-tour-layer__restore">
                <button type="button" mat-flat-button color="primary" class="prep-tour-layer__restore-button"
                  [attr.aria-label]="labels.restore" [attr.title]="labels.restore" (click)="tour.restore()">
                  <mat-icon aria-hidden="true">open_in_full</mat-icon> {{ v.restoreText }}
                </button>
              </div>
              <ng-template #shown>
                <ng-container *ngIf="!v.hidden">
                  <prep-tour-spotlight [rect]="v.rect" [opacity]="dimOpacity" [radius]="radius"
                    [hotspot]="step.advance?.kind === 'click' && !v.missing"
                    [hotspotLabel]="hotspotLabel(step)" [reducedMotion]="reducedMotion"
                    (hotspotClick)="tour.hotspotClick()"></prep-tour-spotlight>
                  <prep-tour-coachmark [step]="step" [index]="v.index" [count]="v.count" [anchor]="v.anchorEl" [rect]="v.rect"
                    [focusAnchor]="v.focusEl" [canNext]="v.canNext" [skippable]="step.skippable !== false"
                    [missing]="v.missing" [reducedMotion]="reducedMotion" [labels]="labels"
                    [progress]="v.guide.progress || 'dots'" [heading]="v.guide.title" [tick]="v.tick"
                    (next)="tour.next()" (back)="tour.back()" (skip)="tour.skip()" (close)="tour.stop('closed')"
                    (minimize)="tour.minimize()" (page)="tour.pagerMove($event)"></prep-tour-coachmark>
                </ng-container>
              </ng-template>
            </ng-container>
          </ng-container>
          <ng-container *ngSwitchCase="'done'">
            <prep-tour-dialog *ngIf="v.completionCard" [content]="v.completionCard" [centered]="true"
              [reducedMotion]="reducedMotion" [labels]="labels"
              (primary)="completeWith(v.completionCard.onPrimary)" (secondary)="completeWith(v.completionCard.onSecondary)"></prep-tour-dialog>
          </ng-container>
        </ng-container>
      </div>
    </ng-container>
    <div class="prep-tour-live cdk-visually-hidden" aria-live="polite" aria-atomic="true">{{ vm ? vm.announcement : '' }}</div>
  `,
  styles: [`
    .prep-tour-outlet { display: contents; }
    .prep-tour-layer { position: fixed; inset: 0; pointer-events: none; }
    .prep-tour-layer__restore { position: absolute; left: 16px; bottom: 16px; pointer-events: auto; }
    .prep-tour-layer__restore-button { box-shadow: 0 6px 20px rgba(0,0,0,.3); border-radius: 999px !important; }
    .prep-tour-layer__restore-button .mat-icon { font-size: 18px; width: 18px; height: 18px; vertical-align: middle; margin-right: 4px; }
    .prep-tour-layer__restore-button:focus-visible { outline: 2px solid #fff; outline-offset: 2px; box-shadow: 0 0 0 4px #1976d2, 0 6px 20px rgba(0,0,0,.3); }
  `],
})
export class PrepTourOutletComponent implements OnInit, OnDestroy {
  @Input() zIndex = 1100;
  @Input() dimOpacity = 0.45;
  @Input() radius = 6;
  @Input() reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  @Input() labels: PrepTourLabels;

  vm: OutletVm | null = null;

  private subs = new Subscription();
  private stopWatch: (() => void) | null = null;
  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private fingerprint = '';
  private tick = 0;
  private keysBound = false;

  constructor(
    readonly tour: PrepTourService,
    private readonly registry: PrepTourAnchorRegistry,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
    @Inject(PREP_TOUR_LABELS) defaultLabels: PrepTourLabels,
  ) {
    this.labels = defaultLabels;
  }

  ngOnInit(): void {
    this.subs.add(this.tour.changes$.subscribe(() => this.render(true)));
    this.subs.add(this.registry.changes$.subscribe(() => this.pollOnce()));
    this.render(true);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.deactivate();
  }

  /** Completion dialog button: run the card's callback (if any), then finish the guide. */
  completeWith(callback?: () => void): void {
    try { if (callback) callback(); } catch { /* consumer callback failed — still finish */ }
    this.tour.finish();
  }

  hotspotLabel(step: PrepTourStep): string {
    return prepTourText(step.actionNote) || this.labels.hotspot;
  }

  // ── Rendering ────────────────────────────────────────────────────────

  /** Rebuild the view model (inside the zone). `force` skips the fingerprint check. */
  private render(force: boolean): void {
    const active = this.tour.active;
    if (!active) {
      this.deactivate();
      if (this.vm) { this.vm = null; this.fingerprint = ''; this.cdr.markForCheck(); }
      return;
    }
    this.activate();
    const next = this.buildVm(active);
    const fp = this.fingerprintOf(next);
    if (!force && fp === this.fingerprint) return;
    this.fingerprint = fp;
    this.tick++;
    next.tick = this.tick;
    this.vm = next;
    this.cdr.markForCheck();
  }

  private buildVm(guide: PrepTourDefinition): OutletVm {
    const step = this.tour.step;
    const missing = this.tour.anchorMissing;
    const target = step ? step.target : undefined;
    const anchorEl = target && !missing ? this.registry.resolve(target) : null;
    const rect = anchorEl && target ? this.registry.rectOf(target) : null;
    const focusTarget = step ? step.focusTarget : undefined;
    let hidden = false;
    try { hidden = !!(step && step.hideWhile && step.hideWhile()); } catch { hidden = false; }
    const count = this.tour.stepCount;
    const index = this.tour.stepIndex;
    const title = step ? prepTourText(step.title) : '';
    const pager = step ? step.pager : undefined;
    return {
      guide,
      phase: this.tour.phase,
      welcome: guide.welcome || null,
      step,
      index,
      count,
      rect,
      anchorEl,
      focusEl: focusTarget ? this.registry.resolve(focusTarget) : null,
      hidden,
      missing,
      minimized: this.tour.minimized,
      canNext: this.tour.canNext,
      completionCard: this.tour.completionCard,
      announcement: step ? this.labels.announce(index + 1, count, title) : '',
      restoreText: pager ? (pager.label ? pager.label() : `${pager.index() + 1} / ${pager.count()}`) : title,
      tick: this.tick,
    };
  }

  /** Everything the card or spotlight renders from; a change means a re-render. */
  private fingerprintOf(v: OutletVm): string {
    const s = v.step;
    const parts: unknown[] = [
      v.phase, v.index, v.count, v.hidden, v.missing, v.minimized, v.canNext,
      v.anchorEl ? 1 : 0, v.focusEl ? 1 : 0, v.completionCard ? v.completionCard.title : '',
    ];
    if (v.rect) parts.push(Math.round(v.rect.left), Math.round(v.rect.top), Math.round(v.rect.width), Math.round(v.rect.height));
    if (s) {
      parts.push(s.id, v.restoreText);
      try {
        parts.push(prepTourText(s.title), prepTourText(s.body), prepTourText(s.actionNote), prepTourText(s.nextLabel));
        parts.push(s.busy ? !!s.busy() : false);
        parts.push(typeof s.skipTo === 'function' ? s.skipTo() : s.skipTo);
        const actions = s.action === undefined ? [] : Array.isArray(s.action) ? s.action : [s.action];
        actions.forEach((a) => parts.push(prepTourText(a.label), prepTourText(a.icon), a.hidden ? !!a.hidden() : false));
        if (s.pager) parts.push(s.pager.index(), s.pager.count());
      } catch { parts.push('err'); }
    }
    return parts.join('\u0001');
  }

  // ── Active-guide plumbing ────────────────────────────────────────────

  private activate(): void {
    if (!this.stopWatch) this.stopWatch = this.registry.watch();
    if (!this.keysBound) {
      document.addEventListener('keydown', this.onKeydown, true);
      this.keysBound = true;
    }
    if (this.pollHandle === null) {
      this.zone.runOutsideAngular(() => {
        this.pollHandle = setInterval(() => this.pollOnce(), RENDER_POLL_MS);
      });
    }
  }

  private deactivate(): void {
    if (this.stopWatch) { this.stopWatch(); this.stopWatch = null; }
    if (this.keysBound) {
      document.removeEventListener('keydown', this.onKeydown, true);
      this.keysBound = false;
    }
    if (this.pollHandle !== null) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  /** Outside the zone: re-enter only when the fingerprint changed. */
  private pollOnce(): void {
    const active = this.tour.active;
    if (!active) return;
    let fp: string;
    try { fp = this.fingerprintOf(this.buildVm(active)); } catch { return; }
    if (fp === this.fingerprint) return;
    this.zone.run(() => this.render(true));
  }

  /**
   * Document-level (capture) keys. Arrow / Enter navigation only applies while
   * focus is inside this outlet's own card or dialog, so the host page keeps
   * its keyboard behaviour (tag tree, menus, canvas). Escape is left to an
   * open Material overlay (menu, dialog…) so it closes that instead of the guide.
   */
  private readonly onKeydown = (event: KeyboardEvent): void => {
    if (!this.tour.active) return;
    const target = event.target as HTMLElement | null;
    const editable = !!target && (EDITABLE.test(target.tagName) || target.isContentEditable);
    const phase = this.tour.phase;
    const step = this.tour.step;
    const fromTourUi = this.fromTourUi(target);
    switch (event.key) {
      case 'Escape':
        if (!fromTourUi && this.overlayOpen(target)) return; // the overlay handles its own Escape
        event.stopImmediatePropagation();
        event.preventDefault();
        this.zone.run(() => {
          if (phase === 'welcome') this.tour.dismiss();
          else if (phase === 'done') this.tour.finish();
          else this.tour.stop('escape');
        });
        return;
      case 'Enter':
      case 'ArrowRight':
        if (!fromTourUi || editable || phase !== 'step' || !this.tour.canNext) return;
        if (step && (step.buttons === false || step.nextButton === false)) return;
        if (event.key === 'Enter' && target && target.tagName === 'BUTTON') return; // let the focused button act
        event.stopImmediatePropagation();
        event.preventDefault();
        this.zone.run(() => this.tour.next());
        return;
      case 'ArrowLeft':
        if (!fromTourUi || editable || phase !== 'step') return;
        if (step && (step.buttons === false || step.backButton === false)) return;
        event.stopImmediatePropagation();
        event.preventDefault();
        this.zone.run(() => this.tour.back());
        return;
    }
  };

  /** The event originated inside this outlet (card, dialog, restore button). */
  private fromTourUi(target: EventTarget | null): boolean {
    const root = this.host.nativeElement;
    return !!root && target instanceof Node && root.contains(target);
  }

  /**
   * A Material / CDK overlay other than a tooltip is open (menu, dialog, select
   * panel…), or the event came from inside the overlay container.
   */
  private overlayOpen(target: EventTarget | null): boolean {
    const container = document.querySelector('.cdk-overlay-container');
    if (!container) return false;
    if (target instanceof Node && container.contains(target)) return true;
    const panes = Array.from(container.querySelectorAll('.cdk-overlay-pane'));
    return panes.some((pane) => {
      const content = pane.firstElementChild;
      return !!content && content.tagName !== 'MAT-TOOLTIP-COMPONENT';
    });
  }
}
